// Task projection reconciliation across independently mounted dashboard surfaces. Task decisions happen in
// the global detail modal while the Kanban remains mounted behind it, so an accepted server-side decision
// invalidates both projections. The event carries no business state: every listener re-reads its own
// server-owned projection rather than applying an optimistic lifecycle transition in the browser.

export const TASK_PROJECTIONS_CHANGED_EVENT = 'ritona:task-projections-changed';

/** Resolve the browser event target once per operation; server/test runtimes have no mounted projections. */
function browserWindow(): Window | null {
  return typeof window === 'undefined' ? null : window;
}

/** Announce that an accepted Task write made currently rendered Task projections stale. */
export function notifyTaskProjectionsChanged(): void {
  const target = browserWindow();
  if (target === null) return;
  try {
    target.dispatchEvent(new Event(TASK_PROJECTIONS_CHANGED_EVENT));
  } catch {
    // This event is only an invalidation hint: callers must keep their authoritative write/read outcome even
    // when a browser is tearing down. No optimistic Task state is applied when the hint cannot be delivered.
  }
}

/** Re-read a mounted Task projection after another Task surface accepts a write. */
export function subscribeToTaskProjectionChanges(reload: () => void): () => void {
  const target = browserWindow();
  if (target === null) return () => undefined;
  target.addEventListener(TASK_PROJECTIONS_CHANGED_EVENT, reload);
  return () => target.removeEventListener(TASK_PROJECTIONS_CHANGED_EVENT, reload);
}
